import { useCallback } from 'react'

import { useTranslation } from 'react-i18next'
import { useMutation, useQuery } from 'react-query'

import { AxiosError } from 'axios'
import sentryService from 'services/sentry'

import { AnamnesisStepName } from '../@types/Patient'
import ServerError from '../@types/ServerError'
import usePage from '../hooks/usePage'
import { useAnswers } from '../providers/AnswersProvider'
import { useFactoringAgreement } from '../providers/FactoringAgreementProvider'
import { usePatient } from '../providers/PatientProvider'
import SignDocumentScreen from '../screens/SignDocument'
import api from '../services/api'
import { getSignedDocumentDownloadUrl } from '../services/api/signedDocuments'

const FACTORING_STEP = AnamnesisStepName.FACTORING_AGREEMENT

const FactoringAgreementPage: React.FC = () => {
  const page = usePage()

  const answers = useAnswers()
  const patient = usePatient()
  const factoringAgreement = useFactoringAgreement()
  const { i18n } = useTranslation()

  const params = {
    patientReferenceId: patient?.data?.referenceId || '',
    step: FACTORING_STEP,
    language: i18n.language?.toUpperCase() || 'DE',
    code: answers.code || '',
  }

  useQuery(['signed-document', params], () => api.signedDocuments.getSignedDocument(params), {
    retry: (count: number, error: AxiosError) => {
      if (error.response?.status === 409) {
        return false
      }

      return count <= 3
    },
    enabled: !!params.patientReferenceId && !!params.code,
    onSuccess: () => {
      factoringAgreement.setPdfLink(getSignedDocumentDownloadUrl(params))
    },
    onError: (error: AxiosError<ServerError>) => {
      if (error.response?.status === 404) {
        sentryService.captureFactoringAgreementNotFoundError({
          error,
          code: answers.code,
          clinic: patient?.data?.clinic,
        })
      }

      if (error.response?.status === 404 || error.response?.status === 409) {
        // Skip the factoring page if the document could not be found (status 404) or was already signed (status 409)
        return page.onSubmit({})
      }

      sentryService.captureGetFactoringAgreementError({
        error,
        code: answers.code,
        clinic: patient?.data?.clinic,
      })
    },
  })

  const { mutateAsync: signDocument, ...signDocumentStatus } = useMutation(api.signedDocuments.signSignedDocument, {
    onSuccess: () => {
      page.onSubmit({})
    },
    onError: (error: AxiosError<ServerError>) => {
      if (error.response?.status === 409) {
        page.onSubmit({})

        return
      }

      sentryService.captureSignFactoringError({
        error,
        code: answers.code,
        clinic: patient?.data?.clinic,
        extra: {
          anamnesisState: patient?.data?.anamnesisState,
        },
      })
    },
  })

  const handleSignatureSubmit = useCallback(
    (signature: string) => {
      if (patient?.data?.referenceId) {
        signDocument({
          patientReferenceId: patient.data.referenceId,
          step: FACTORING_STEP,
          language: i18n.language?.toUpperCase() || 'DE',
          code: answers.code || '',
          signatureEncoded: signature.replace('data:image/png;base64,', ''),
        })
      } else {
        throw new Error("Can't submit factoring agreement without patient.referenceId")
      }
    },
    [i18n.language, answers.code, patient, signDocument]
  )

  return (
    <SignDocumentScreen
      {...page}
      onSignatureSubmit={handleSignatureSubmit}
      pdfLink={factoringAgreement.pdfLink}
      signatureLoading={signDocumentStatus.isLoading}
      titleKey="screen.factoring_agreement.title"
      subtitleKey="subcategory.convenient_payment"
      descriptionKey="screen.factoring_agreement.description_v2"
    />
  )
}

export default FactoringAgreementPage
